import React, { useEffect } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import { useLazyGetRulesAffectedByGeofenceQuery } from "services/rules";

// Shared confirmation dialog surfaced before any UI
// action that deletes a global geofence. Queries rulesMS for every rule
// whose `locationConditions` references this geofence's `globalUUID` and
// informs the user that those rules will have the constraint removed. The
// backend cleanup runs asynchronously via a durable work-queue message
// fired from locationMS; the rule itself continues to exist — just without
// the dangling geofence reference.
//
// A rule with an `"Include"` operator and only one geofence condition
// becomes effectively unconstrained by location after the pull; worth
// flagging to the user. A rule with an `"Exclude"` operator and only one
// geofence similarly becomes unconstrained. Multi-geofence rules just
// shrink the list.
//
// Usage:
//   <GeofenceDeleteWarning
//     open={confirmOpen}
//     globalUUID={geofence.globalUUID}
//     geofenceName={geofence.name}
//     onCancel={() => setConfirmOpen(false)}
//     onConfirm={() => { setConfirmOpen(false); actuallyDelete(); }}
//   />
export default function GeofenceDeleteWarning({
  open,
  globalUUID,
  geofenceName,
  onCancel,
  onConfirm,
}) {
  const token = window.localStorage.getItem("token");
  const [fetchAffected, { data, isFetching, isUninitialized }] =
    useLazyGetRulesAffectedByGeofenceQuery();

  useEffect(() => {
    if (open && globalUUID) {
      fetchAffected({ token, globalUUID });
    }
  }, [open, globalUUID]);

  const rawAffected = data?.payload || [];

  // A global rule is persisted as N per-device documents sharing a
  // `globalId` — each references the same geofence, so the raw list shows
  // the same rule name N times. Dedupe by `globalId` (falling back to
  // `_id` for device-level rules without a globalId) so the user sees one
  // entry per logical rule.
  const affected = (() => {
    const seen = new Set();
    const out = [];
    for (const rule of rawAffected) {
      const key = rule.globalId || String(rule._id);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(rule);
    }
    return out;
  })();

  // A rule becomes "unconstrained by location" after the pull when its
  // locationConditions array had exactly one entry (the one being removed).
  const becomesUnconstrained = (rule) => {
    const conds = rule?.locationConditions || [];
    return (
      conds.length === 1 &&
      conds.some((c) => c.globalUUID === globalUUID)
    );
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>
        Delete geofence{geofenceName ? ` "${geofenceName}"` : ""}
      </DialogTitle>
      <DialogContent>
        {isFetching || isUninitialized ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "24px",
            }}
          >
            <CircularProgress />
          </div>
        ) : affected.length === 0 ? (
          <p>
            No existing rules reference this geofence. Deletion will proceed
            without touching any rule.
          </p>
        ) : (
          <>
            <p>
              <strong>
                {affected.length} rule{affected.length === 1 ? "" : "s"}
              </strong>{" "}
              currently reference this geofence. Deleting it will remove the
              geofence entry from each rule's location constraints. Rules
              that had only this one geofence as a constraint will no longer
              be scoped by location - they will fire regardless of device
              location.
            </p>
            <ul
              style={{
                maxHeight: "220px",
                overflowY: "auto",
                paddingLeft: "16px",
              }}
            >
              {affected.map((rule) => (
                <li key={rule._id}>
                  <strong>{rule.name || "(unnamed)"}</strong>
                  {becomesUnconstrained(rule)
                    ? " - will no longer be scoped by location"
                    : ""}
                </li>
              ))}
            </ul>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="error">
          Cancel
        </Button>
        <Button onClick={onConfirm} color="secondary" disabled={isFetching}>
          Delete geofence
        </Button>
      </DialogActions>
    </Dialog>
  );
}
